import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, destroy } from 'redux-form';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';

import { actionCreators } from 'react/api/actions/wizard';
import { getResourceTo } from 'react/utils/utils';
import CancelModal from 'react/containers/modals/FormWizard/CancelModal';

const FormWizard = (props) => {
  const {
    entity,
    tabName,
    sections,
    page,
    beforeSubmit,
    goToPreviousSectionAction,
    goToNextSectionAction,
    sidebar,
    handleSubmit,
    customRequest,
    openCancelModalAction,
    loadingInitialValues,
    saveFormErrorAction,
    saving
  } = props;

  useEffect(() => {
    const {
      clearFormStateAction,
      destroyFormAction
    } = props;

    destroyFormAction('SamlConfiguration');
    clearFormStateAction({ entity });
  }, []);

  const onSubmit = (values) => {
    const sectionsLength = sections.length - 1;

    const finalFormValues = beforeSubmit(values);

    if (!finalFormValues) {
      saveFormErrorAction({ entity, customError: 'general.form.submitError' });
      return;
    }

    goToNextSectionAction({
      entity,
      page,
      sectionsLength
    });
  };

  const saveForm = (values) => {
    const finalFormValues = beforeSubmit(values, 'saveForm');

    if (!finalFormValues) {
      saveFormErrorAction({ entity, customError: 'general.form.submitError' });
    } else if (customRequest) {
      customRequest(finalFormValues);
    }
  };

  const openCancelModal = () => {
    openCancelModalAction({
      entity
    });
  };

  const previousSection = () => {
    goToPreviousSectionAction({
      entity,
      page
    });
  };

  const renderErrors = () => {
    const {
      submitFailed,
      valid,
      errorMessage
    } = props;

    let className = '';
    className = errorMessage || submitFailed ? 'showError' : className;
    className = ((submitFailed && valid) || saving) ? 'hideError' : className;

    return (
      <div className={`formError ${className}`}>
        { getResourceTo('general.form.submitError') }
      </div>
    );
  };

  const renderCancelButton = () => {
    const label = getResourceTo('samlConfiguration.button.cancel');
    return (
      <input type="button" value={label} className="btn-secondary cancelButton" onClick={openCancelModal} />
    );
  };

  const renderFinishButton = () => {
    const saveClassName = loadingInitialValues || saving ? 'submitting' : 'nextButton';
    const label = getResourceTo('samlConfiguration.button.finish');
    return (
      <input className={saveClassName} type="button" value={label} onClick={handleSubmit(saveForm)} />
    );
  };

  const renderPreviousButton = () => {
    const label = getResourceTo('samlConfiguration.button.previous');
    return (
      <input type="button" value={label} onClick={previousSection} className="btn-secondary previousButton" />
    );
  };

  const renderNextButton = () => {
    const saveClassName = loadingInitialValues || saving ? 'submitting' : 'nextButton';
    const label = getResourceTo('samlConfiguration.button.next');
    return (
      <input className={saveClassName} type="button" value={label} onClick={handleSubmit(onSubmit)} />
    );
  };

  const helpOnClick = () => {
    const help = new Monet.UI.Help(entity, getResourceTo(tabName));
    help.logIn();
  };

  const renderHelpButton = () => (
    <div className="formHelpButton">
      <button className="MonetUIHelpLink" onClick={helpOnClick} type="button" aria-label="help" />
    </div>
  );

  const renderFormButtons = ({ showHelp } = {}) => {
    const isInInitialPage = page === 0;
    const isInFinish = page === sections.length - 1;
    return (
      <div className="formButtons">
        {renderCancelButton()}
        {!isInInitialPage && renderPreviousButton()}
        {!isInFinish ? renderNextButton() : renderFinishButton()}
        {showHelp ? renderHelpButton() : null}
      </div>
    );
  };

  return (
    <form className="generalForm wizard">
      <CancelModal {...props} />
      {sidebar && sidebar()}
      <div className="sections">
        {renderErrors()}
        {renderFormButtons({ showHelp: true })}
        {sections[page]}
        {renderFormButtons()}
      </div>
    </form>
  );
};

FormWizard.propTypes = {
  entity: PropTypes.string.isRequired,
  tabName: PropTypes.string.isRequired,
  sections: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  beforeSubmit: PropTypes.func,
  goToNextSectionAction: PropTypes.func.isRequired,
  goToPreviousSectionAction: PropTypes.func.isRequired,
  sidebar: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  customRequest: PropTypes.func,
  openCancelModalAction: PropTypes.func.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  loadingInitialValues: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  saveSuccess: PropTypes.bool.isRequired,
  saveError: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  saveFormErrorAction: PropTypes.func.isRequired,
  showHelp: PropTypes.bool
};

FormWizard.defaultProps = {
  beforeSubmit: (values) => values,
  sidebar: null,
  customRequest: null,
  errorMessage: '',
  showHelp: false
};

const form = reduxForm({
  enableReinitialize: true,
  keepDirtyOnReinitialize: true
})(FormWizard);

const mapStateToProps = (state, ownProps) => ({
  initialValues: state.wizard.getIn([ownProps.entity, 'initialValues'], fromJS({})).toJSON(),
  saving: state.wizard.getIn([ownProps.entity, 'saving'], false),
  saveSuccess: state.wizard.getIn([ownProps.entity, 'saveSuccess'], false),
  saveError: state.wizard.getIn([ownProps.entity, 'saveError'], false),
  page: state.wizard.getIn([ownProps.entity, 'page'], 0),
  errorMessage: state.wizard.getIn([ownProps.entity, 'errorMessage'], ''),
  loadingInitialValues: state.wizard.getIn([ownProps.entity, 'loadingInitialValues'], false),
  form: ownProps.entity
});

const mapDispatchToProps = {
  goToNextSectionAction: actionCreators.goToNextSection,
  goToPreviousSectionAction: actionCreators.goToPreviousSection,
  openCancelModalAction: actionCreators.openCancelModal,
  saveFormErrorAction: actionCreators.saveFormError,
  clearFormStateAction: actionCreators.clearFormState,
  destroyFormAction: destroy
};

export default connect(mapStateToProps, mapDispatchToProps)(form);
